import { Component, Inject, inject, ViewChild } from '@angular/core';
import { Address, BillingDetails, PaymentPlatform } from '@aiwyn/api';
import { BehaviorSubject } from 'rxjs';
import { DialogSize, extractFailureContext, ignoreFalsy } from '@aiwyn/shared';
import {
  PaymentMethodSurface,
  PaymentMethodTrackService,
  PaymentMethodWorkflowEvent,
} from '../../tracking';
import { switchMap, take, tap } from 'rxjs/operators';
import { AbstractPaymentMethod, PaymentPlatformService } from '../../platforms';
// TODO: Temporary workaround for a circular dep that breaks unit tests.
import { StripeAddressElementComponent } from '../../platforms/stripe/components/stripe-address-element/stripe-address-element.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthStateAdaptorService } from '@aiwyn/auth';

export interface UpdateBillingAddressDialogData {
  paymentMethod: AbstractPaymentMethod;
  /**
   * Where the update flow was opened from. Stamped on every PAYMENT_METHOD_* event via the
   * per-container `PaymentMethodTrackService` envelope.
   */
  surface: PaymentMethodSurface;
}

export type UpdateBillingAddressDialogRef = MatDialogRef<UpdateBillingAddressDialogComponent, boolean>;

@Component({
  selector: 'app-update-billing-address-dialog',
  templateUrl: './update-billing-address-dialog.component.html',
  styleUrls: ['./update-billing-address-dialog.component.scss'],
  standalone: false,
  providers: [PaymentMethodTrackService],
})
export class UpdateBillingAddressDialogComponent {
  readonly DialogSize = DialogSize;
  readonly PaymentPlatform = PaymentPlatform;

  @ViewChild(StripeAddressElementComponent) stripeAddressElement: StripeAddressElementComponent;

  isSaveLoading$ = new BehaviorSubject<boolean>(false);

  private readonly paymentMethodTrackService = inject(PaymentMethodTrackService);
  private readonly authStateAdaptor = inject(AuthStateAdaptorService);

  // Set to true once a terminal PAYMENT_METHOD_* event has fired (UPDATE_SUCCEEDED or UPDATE_FAILED).
  // afterClosed reads this to decide whether to emit PAYMENT_METHOD_UPDATE_CANCELLED.
  private _didEmitTerminalEvent = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UpdateBillingAddressDialogData,
    public readonly paymentPlatformService: PaymentPlatformService,
    public dialogRef: MatDialogRef<UpdateBillingAddressDialogComponent, boolean>,
  ) {
    this.paymentMethodTrackService.setSurface(this.data.surface);
    this.paymentMethodTrackService.setUserType(this.authStateAdaptor.getUserTypeSnapshot());

    const { id: paymentMethodId, subType, type } = this.data.paymentMethod;
    const paymentMethodType = (subType ?? type ?? '').toString().toUpperCase();

    this.paymentMethodTrackService.trackWorkflow(
      PaymentMethodWorkflowEvent.PAYMENT_METHOD_UPDATE_STARTED,
      { paymentMethodId, paymentMethodType },
    );

    // Catches every dismiss path (Cancel button, X, ESC, backdrop) in one place.
    this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      if (!this._didEmitTerminalEvent) {
        this.paymentMethodTrackService.trackWorkflow(
          PaymentMethodWorkflowEvent.PAYMENT_METHOD_UPDATE_CANCELLED,
          { paymentMethodId, paymentMethodType },
        );
      }
    });
  }

  save(): void {
    const { id: paymentMethodId, subType, type } = this.data.paymentMethod;
    const paymentMethodType = (subType ?? type ?? '').toString().toUpperCase();
    const operationKey = `PAYMENT_METHOD_UPDATE:${paymentMethodId}`;
    this.isSaveLoading$.next(true);

    this.stripeAddressElement.validate().pipe(
      take(1),
      tap(isValid => {
        this.isSaveLoading$.next(isValid);
        if (isValid) {
          this.paymentMethodTrackService.startOperation(operationKey);
        }
      }),
      ignoreFalsy(),
      switchMap(_ => this.paymentPlatformService.getNewBillingDetails()),
      switchMap(newBillingDetails => this.paymentPlatformService.updatePaymentMethod(paymentMethodId, newBillingDetails).pipe(
        tap(_ => {
          this._didEmitTerminalEvent = true;
          this.paymentMethodTrackService.trackWorkflow(
            PaymentMethodWorkflowEvent.PAYMENT_METHOD_UPDATE_SUCCEEDED,
            {
              paymentMethodId,
              paymentMethodType,
              changedFields: this._computeChangedFields(this.data.paymentMethod.billingDetails, newBillingDetails),
              operationDurationMs: this.paymentMethodTrackService.endOperation(operationKey),
            },
          );
        }),
      )),
      switchMap(_ => this.stripeAddressElement.onClose()),
      tap({
        next: _ => this.dialogRef.close(true),
        error: error => {
          this._didEmitTerminalEvent = true;
          this.isSaveLoading$.next(false);
          this.paymentMethodTrackService.trackWorkflow(
            PaymentMethodWorkflowEvent.PAYMENT_METHOD_UPDATE_FAILED,
            {
              ...extractFailureContext(error),
              paymentMethodId,
              paymentMethodType,
              operationDurationMs: this.paymentMethodTrackService.endOperation(operationKey),
            },
          );
        },
      }),
    ).subscribe();
  }

  // Fixed iteration order so the comma-wrapped string is stable for dashboard filters.
  private static readonly TOP_LEVEL_FIELDS: readonly Exclude<keyof BillingDetails, 'address'>[] = ['name', 'email', 'phone'];
  private static readonly ADDRESS_FIELDS: readonly (keyof Address)[] = ['line1', 'line2', 'city', 'state', 'postalCode', 'country'];

  private _computeChangedFields(oldDetails: BillingDetails | undefined, newDetails: BillingDetails | undefined): string {
    const changed: string[] = [];
    for (const field of UpdateBillingAddressDialogComponent.TOP_LEVEL_FIELDS) {
      if ((oldDetails?.[field] ?? '') !== (newDetails?.[field] ?? '')) {
        changed.push(field);
      }
    }
    if (!this._addressEqual(oldDetails?.address, newDetails?.address)) {
      changed.push('address');
    }
    // Wrap with commas so dashboards can filter `contains ',name,'` without substring false
    // matches. Empty case is the empty string, matching the [[auth-tracking-context]]
    // client-id formatter — no-change events simply won't match any specific-field filter.
    return changed.length === 0 ? '' : `,${changed.join(',')},`;
  }

  private _addressEqual(a: Address | undefined, b: Address | undefined): boolean {
    return UpdateBillingAddressDialogComponent.ADDRESS_FIELDS.every(field => (a?.[field] ?? '') === (b?.[field] ?? ''));
  }
}
